import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import React, { useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { formatBytes } from '@sophocles/utils/formatUtils';

// Generic upload / edit dialog for the BinaryFile endpoint
// (`/api/odl-app/binary-files/`).  Parameterized by
// (entityType, entityId, fileType) so the same component backs every
// surface — customer attachments, customer logo, site photos, etc.
//
// Modes:
//   - mode="create"  → drag-drop / click-to-pick file region required,
//     comment optional.  Submits a multipart FormData with
//     entity_type, entity_id, file_type, file, comment.
//   - mode="edit"    → comment-only by design (file replace would be a
//     separate UX gesture; not exposed here).
//
// Caller wires `onCreate(formData)` / `onUpdate(guid, body)` and
// receives an `onSaved()` callback after success.

const createSchema = yup.object({
  file: yup.mixed().required('A file is required.'),
  comment: yup.string(),
});
const editSchema = yup.object({
  comment: yup.string(),
});

// Inner drop region — the meat of the upload dialog when no file is
// chosen yet.  Once a file is picked, swaps to a compact "selected
// file" card with a clear button.
function FileDropField({ value, onChange }) {
  const { t } = useTranslation();
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openPicker,
  } = useDropzone({
    onDrop: accepted => {
      if (accepted?.[0]) onChange(accepted[0]);
    },
    multiple: false,
    noClick: Boolean(value),
    noKeyboard: Boolean(value),
  });

  if (value) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          borderRadius: 'var(--radius-md)',
          border: 1,
          borderColor: 'success.light',
          backgroundColor: theme => alpha(theme.palette.success.main, 0.06),
          px: 2,
          py: 1.5,
        }}
      >
        <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {value.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatBytes(value.size)}
          </Typography>
        </Stack>
        <Button size="small" variant="text" onClick={openPicker}>
          {t('binaryFiles.replace', 'Replace')}
        </Button>
        <IconButton size="small" onClick={() => onChange(null)} aria-label={t('actions.remove', 'Remove')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  }

  return (
    <Box
      {...getRootProps()}
      sx={{
        borderRadius: 'var(--radius-md)',
        border: 2,
        borderStyle: 'dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        backgroundColor: theme => (isDragActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent'),
        px: 3,
        py: 4,
        cursor: 'pointer',
        textAlign: 'center',
        transition:
          'border-color var(--duration-normal) var(--easing-standard), background-color var(--duration-normal) var(--easing-standard)',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <input {...getInputProps()} />
      <Stack alignItems="center" spacing={1}>
        <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: 'primary.main' }} />
        <Typography variant="subtitle2">
          {isDragActive
            ? t('binaryFiles.dropToUpload', 'Drop file to upload')
            : t('binaryFiles.dropOrBrowse', 'Drag and drop a file, or click to browse')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('binaryFiles.singleFile', 'One file at a time.')}
        </Typography>
      </Stack>
    </Box>
  );
}

export default function BinaryFileDialog({
  open,
  mode = 'create',
  binaryFile = null, // edit mode: { guid, original_filename, comment, ... }
  prefillFile = null, // create mode: optional File handed in by parent (drag-drop)
  entityType,
  entityId,
  fileType,
  onClose,
  onCreate,
  onUpdate,
  onSaved,
  titleCreate,
  titleEdit,
}) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';
  const initialValues = isEdit ? { comment: binaryFile?.comment ?? '' } : { file: prefillFile ?? null, comment: '' };

  const handleSubmit = async values => {
    if (isEdit) {
      const result = await onUpdate(binaryFile.guid, { comment: values.comment });
      if (result?.error) return;
    } else {
      const formData = new FormData();
      formData.append('entity_type', entityType);
      formData.append('entity_id', String(entityId));
      formData.append('file_type', fileType);
      formData.append('file', values.file);
      if (values.comment) formData.append('comment', values.comment);
      const result = await onCreate(formData);
      if (result?.error) return;
    }
    onSaved?.();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={isEdit ? editSchema : createSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ submitForm, isSubmitting, isValid, dirty, values, setFieldValue }) => (
          <Form>
            <DialogTitle sx={{ pr: 6 }}>
              {isEdit
                ? (titleEdit ?? t('binaryFiles.editTitle', 'Edit File'))
                : (titleCreate ?? t('binaryFiles.uploadTitle', 'Upload File'))}
              <IconButton
                aria-label={t('actions.close', 'Close')}
                onClick={onClose}
                size="small"
                sx={{ position: 'absolute', right: 12, top: 12 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <PrefillSync prefillFile={prefillFile} setFieldValue={setFieldValue} />
              <Stack spacing={2.5} sx={{ pt: 1 }}>
                {isEdit ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      borderRadius: 'var(--radius-md)',
                      border: 1,
                      borderColor: 'divider',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('binaryFiles.filename', 'File')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {binaryFile?.original_filename || '—'}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <FileDropField value={values.file} onChange={f => setFieldValue('file', f)} />
                )}
                <Field name="comment">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      label={t('binaryFiles.comment', 'Comment')}
                      multiline
                      minRows={3}
                      fullWidth
                      size="small"
                      placeholder={t('binaryFiles.commentPlaceholder', 'Optional — context for this file')}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error ? meta.error : ''}
                      // Filled multiline centers the textarea within the InputBase root by
                      // default; anchor it to the top so short text lands on line 1 instead
                      // of floating mid-slot.
                      sx={{
                        '& .MuiInputBase-root.MuiInputBase-multiline': { alignItems: 'flex-start' },
                        '& .MuiInputBase-inputMultiline': { alignSelf: 'flex-start', padding: '0 !important' },
                      }}
                    />
                  )}
                </Field>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>{t('actions.cancel', 'Cancel')}</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={submitForm}
                disabled={!isValid || isSubmitting || (isEdit && !dirty)}
              >
                {isEdit ? t('actions.save', 'Save') : t('actions.upload', 'Upload')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

// Sync the parent's `prefillFile` prop into Formik state on open.  Required
// because `enableReinitialize` only fires on `initialValues` identity
// changes, and we want the dropped-from-list file to land in the form
// even when the dialog stays mounted across opens.
function PrefillSync({ prefillFile, setFieldValue }) {
  useEffect(() => {
    if (prefillFile) setFieldValue('file', prefillFile);
  }, [prefillFile, setFieldValue]);
  return null;
}
